import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import merge from 'lodash.merge';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos';

import Background from 'components/Layout/Background';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import Switch from 'components/FormFields/Switch';

import { WorkflowPersonalDataStore } from 'stores/workflowStores/workflowPersonalDataStore/workflowPersonalDataStore';
import { Notifications } from 'stores/workflowStores/workflowPersonalDataStore/model';

interface Props {
  store: WorkflowPersonalDataStore;
  ltip: boolean;
}

const Notification: React.FC<Props> = ({ store, ltip }) => {
  const { t } = useTranslation();
  const [notificationsConfirm, setNotificationsConfirm] = useState(false);
  const [change, setChange] = useState(false);
  const [notificationChange, setNotificationChange] = useState(false);

  const handleSubmitNotification = async (values: Notifications) => {
    store.handleSaveNotifications(values);
    setChange(false);
    setNotificationChange(false);
  };

  const resetChange = () => {
    setChange(true);
    setNotificationChange(true);
  };

  return (
    <Formik
      initialValues={merge(
        {},
        {
          notificationMonthlyStatement: true,
          notificationYearlyStatement: true,
          ltipSummary: true,
          newPayment: true,
          paymentSent: true,
          targetClose: true,
          targetWithdrawal: true,
          depositDue: true,
          depositClose: true,
          showFullOverview: false,
        },
        store.data!.notification,
      )}
      enableReinitialize
      onSubmit={handleSubmitNotification}
    >
      {(formikProps) => (
        <Form>
          <Background padding={4}>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="h3">{t('client_emailNotifications_short')}</Typography>
              </Grid>
              <Grid container item xs={12} sm={6}>
                <Grid item xs={12}>
                  <Switch
                    name={`notificationMonthlyStatement`}
                    label={`${t('pdf_document_MONTH')}`}
                    afterChange={resetChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Switch
                    name={`notificationYearlyStatement`}
                    label={`${t('pdf_document_YEAR')}`}
                    afterChange={resetChange}
                  />
                </Grid>
                {ltip && (
                  <Grid item xs={12}>
                    <Switch
                      name={`ltipSummary`}
                      label={`${t('pdf_document_LTIP_SUMMARY')}`}
                      afterChange={resetChange}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Switch
                    name={`ltipSummary`}
                    label={`${t('pdf_document_LTIP_SUMMARY')}`}
                    afterChange={resetChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Switch
                    name={`newPayment`}
                    label={`${t('client_emailNotification_newPayment')}`}
                    afterChange={resetChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Switch name={`paymentSent`} label={`${t('client_emailNotification_paymentSent')}`} disabled />
                </Grid>
                <Grid item xs={12}>
                  <Switch
                    name={`targetClose`}
                    label={`${t('client_emailNotification_targetClose')}`}
                    afterChange={resetChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Switch
                    name={`targetWithdrawal`}
                    label={`${t('client_emailNotification_targetWithdrawal')}`}
                    afterChange={resetChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Switch
                    name={`depositDue`}
                    label={`${t('client_emailNotification_depositDue')}`}
                    afterChange={resetChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Switch
                    name={`depositClose`}
                    label={`${t('client_emailNotification_depositClose')}`}
                    afterChange={resetChange}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h3">{t('client_reports')}</Typography>
              </Grid>
              <Grid container item xs={12} sm={6}>
                <Grid item xs={12}>
                  <Switch
                    name={`showFullOverview`}
                    label={t('client_showFullOverview')}
                    afterChange={() => setChange(true)}
                    tooltipContent={t('client_showFullOverview_tooltip')}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setNotificationsConfirm(true)}
                  endIcon={<ArrowForwardIos fontSize="small" />}
                  disabled={!change}
                >
                  {t('general_save')}
                </Button>
              </Grid>
            </Grid>
          </Background>
          <ConfirmDialog
            title={null}
            isOpen={notificationsConfirm}
            confirm={async () => {
              await handleSubmitNotification(formikProps.values);
              setNotificationsConfirm(false);
            }}
            btnCloseText="general_back"
            btnConfirmText="general_confirm"
            close={() => setNotificationsConfirm(false)}
            icons
            noHeader
          >
            {t(notificationChange ? 'client_emailNotifications_confirm' : 'client_reports_confirm')}
          </ConfirmDialog>
        </Form>
      )}
    </Formik>
  );
};

export default Notification;
